// Business-events ("watcher") campaign helpers shared by the wizard, the
// campaign detail and the lead card. Mirrors the WATCHER_* constants in
// backend/apps/autoleads/constants.py.
import { DEPARTEMENT_OPTIONS, NAF_OPTIONS } from "../components/pappers-options"

import type {
  WatcherConfig,
  WatcherEventSnapshot,
  WatcherEventType,
} from "@/nango/types/apps.autoleads/autoleads/AutoLeads"

export const WATCHER_EVENT_TYPES: WatcherEventType[] = [
  "creation",
  "reprise",
  "mise_en_activite",
]

export const WATCHER_LOOKBACK_DAYS_DEFAULT = 30
export const WATCHER_LOOKBACK_OPTIONS = [0, 7, 14, 30, 60, 90] as const

// Same shapes the serializer accepts (WATCHER_*_RE backend-side).
export const WATCHER_NAF_CODE_RE = /^\d{2}(\.\d{1,2}[A-Z]?)?$/
export const WATCHER_POSTAL_CODE_RE = /^\d{5}$/
export const WATCHER_MAX_NAF_CODES = 40
export const WATCHER_MAX_POSTAL_CODES = 200

// A "creation" whose Google listing already holds this many reviews is most
// likely an existing business a new company took over, not a new opening.
export const WATCHER_LIKELY_TAKEOVER_MIN_REVIEWS = 50

export const DEFAULT_WATCHER_CONFIG: WatcherConfig = {
  departements: [],
  postal_codes: [],
  naf_codes: [],
  event_types: ["creation", "reprise"],
  include_contactless: false,
  lookback_days: WATCHER_LOOKBACK_DAYS_DEFAULT,
}

// A new establishment Google already knew: the registry calls it a creation
// (or an activity start), the reviews say the business was running. Either
// the listing was reviewed well before the event (``listed_since``), or it is
// well known under another name.
export function isLikelyTakeover(event: WatcherEventSnapshot): boolean {
  if (event.event_type === "reprise") return false
  if (event.listed_since) return true
  return (
    event.event_type === "creation" &&
    typeof event.listed_reviews === "number" &&
    event.listed_reviews >= WATCHER_LIKELY_TAKEOVER_MIN_REVIEWS
  )
}

// The serializer's rule: a zone (departement or postal code), an activity and
// an event type.
export function watcherConfigIsComplete(config: WatcherConfig): boolean {
  return (
    config.departements.length + config.postal_codes.length > 0 &&
    config.naf_codes.length > 0 &&
    config.event_types.length > 0
  )
}

// A campaign's stored config (an empty object on another source) in the full
// shape the wizard edits.
export function toWatcherConfig(
  stored: Partial<WatcherConfig> | null | undefined,
): WatcherConfig {
  return { ...DEFAULT_WATCHER_CONFIG, ...(stored ?? {}) }
}

// A NAF code typed or picked by the user, in the stored form ("56.10a" ->
// "56.10A"), or null when it is neither a code nor a prefix.
export function normalizeNafCode(raw: string): string | null {
  // Option labels start with the code ("56.10A - Restauration..."): keep it.
  const code = raw.trim().split(" ")[0]?.toUpperCase() ?? ""
  return WATCHER_NAF_CODE_RE.test(code) ? code : null
}

const NAF_LABEL = new Map(NAF_OPTIONS.map((o) => [o.value, o.label]))
const DEPARTEMENT_LABEL = new Map(
  DEPARTEMENT_OPTIONS.map((o) => [o.value, o.label]),
)

// "56.10A - Restauration traditionnelle" for a known code, the bare code for a
// prefix ("56").
export function nafLabel(code: string): string {
  return NAF_LABEL.get(code) ?? code
}

// The activity name without its code ("Restauration traditionnelle").
export function nafShortLabel(code: string): string {
  const label = NAF_LABEL.get(code)
  return label ? label.replace(/^\S+\s+-\s+/, "") : code
}

export function departementLabel(code: string): string {
  return DEPARTEMENT_LABEL.get(code) ?? code
}

// The departement name without its code ("Rhône").
export function departementShortLabel(code: string): string {
  const label = DEPARTEMENT_LABEL.get(code)
  return label ? label.replace(/^\S+\s+-\s+/, "") : code
}

// "Rhône", or "Rhône +2" when the list holds more.
export function firstWithMore(values: string[]): string {
  if (values.length === 0) return ""
  return values.length === 1 ? values[0] : `${values[0]} +${values.length - 1}`
}
